<template>
	<div class="industry-chain-page">
		<div class="chain-grid">
			<section class="chain-panel upstream-panel">
				<div class="panel-title"><div>上游种养原料基地</div></div>
				<div class="panel-body upstream-body">
					<div class="origin-group" v-for="group in originGroups" :key="group.title">
						<div class="source-node">{{ group.title }}</div>
						<div class="chain-list">
							<div class="category-node" v-for="item in group.categories" :key="item.name"
								:class="{ selected: selectedNode.name === item.name }" @click="selectNode('upstream', item)">
								<span>{{ item.name }}</span>
								<i></i>
								<div class="sub-list">
									<em v-for="child in item.children" :key="child" :class="{ active: selectedNode.name === child }"
										@click.stop="selectChild(child, item)">{{ child }}</em>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<div class="chain-arrow" aria-hidden="true">»</div>

			<section class="chain-panel middle-panel">
				<div class="panel-title"><div>中游生产加工</div></div>
				<div class="panel-body function-body middle-body">
					<!-- 节点 tabs：一行显示 -->
					<div class="node-tabs">
						<div class="node-tab" v-for="item in processNodes" :key="item.name"
							:class="{ active: selectedMiddleNode && selectedMiddleNode.name === item.name }"
							@click="selectMiddleNode(item)">
							<span class="tab-name">{{ item.name }}</span>
							<span class="tab-count">{{ item.count }}</span>
						</div>
					</div>

					<!-- 品类 pills -->
					<div class="category-pills" v-if="selectedMiddleNode">
						<div class="pill-label">品类</div>
						<div class="pill-list">
							<div class="pill" v-for="cat in selectedMiddleNode.categories" :key="cat.name"
								:class="{ active: selectedCategory && selectedCategory.name === cat.name }"
								@click="selectCategory(cat)">{{ cat.name }}</div>
						</div>
					</div>

					<!-- 概览指标条 -->
					<div class="metric-strip" v-if="!selectedCategory">
						<div><b>产能利用率</b><strong>86%</strong></div>
						<div><b>合规企业</b><strong>128家</strong></div>
						<div><b>预警数量</b><strong>3条</strong></div>
					</div>

					<!-- 选中品类后的 5 段详情 -->
					<div class="middle-detail" v-if="selectedCategory">
						<div class="detail-section">
							<div class="section-head"><span>产能分布</span></div>
							<div class="bar-chart">
								<div class="bar-row" v-for="b in selectedCategory.capacity" :key="b.name">
									<span class="bar-label">{{ b.name }}</span>
									<div class="bar-track"><i :style="{ width: b.value + '%' }"></i></div>
									<span class="bar-val">{{ b.value }}%</span>
								</div>
							</div>
						</div>

						<div class="detail-section">
							<div class="section-head"><span>产品品类结构</span></div>
							<div ref="structureChart" class="structure-chart"></div>
						</div>

						<div class="detail-section">
							<div class="section-head"><span>区域产能互补</span></div>
							<div class="region-grid">
								<div class="region-cell" v-for="r in selectedCategory.regionSupply" :key="r.region" :class="r.status">
									<b>{{ r.region }}</b>
									<span>{{ r.value }}</span>
								</div>
							</div>
						</div>

						<div class="detail-section">
							<div class="section-head"><span>产品外销流向</span></div>
							<div class="flow-list">
								<div class="flow-row" v-for="f in selectedCategory.exportFlow" :key="f.to">
									<span class="flow-from">重庆</span>
									<i class="flow-arrow"></i>
									<span class="flow-to">{{ f.to }}</span>
									<strong>{{ f.volume }}</strong>
								</div>
							</div>
						</div>

						<div class="detail-section alert-section"
							:class="{ is_warn: selectedCategory.alert.level === 'warn', is_danger: selectedCategory.alert.level === 'danger' }">
							<div class="section-head"><span>产能过剩/缺口预警</span>
								<span class="level-badge" :class="selectedCategory.alert.level">{{ selectedCategory.alert.levelLabel }}</span>
							</div>
							<div class="alert-body">
								<p>{{ selectedCategory.alert.message }}</p>
								<div class="alert-metrics">
									<div><b>过剩</b><strong>{{ selectedCategory.alert.surplus }}</strong></div>
									<div><b>缺口</b><strong>{{ selectedCategory.alert.gap }}</strong></div>
									<div><b>建议</b><strong>{{ selectedCategory.alert.suggest }}</strong></div>
								</div>
							</div>
						</div>
					</div>

					<!-- 未选中节点时 -->
					<div class="detail-card empty-card" v-if="!selectedMiddleNode">
						<h3>生产加工能力</h3>
						<p>点击顶部节点查看各品类产能分布、品类结构、区域互补、外销流向与预警情况。</p>
					</div>

					<!-- 选中节点但未选品类时 -->
					<div class="detail-card" v-else-if="!selectedCategory">
						<h3>{{ selectedMiddleNode.name }}</h3>
						<p>{{ selectedMiddleNode.desc }}</p>
					</div>
				</div>
			</section>

			<div class="chain-arrow" aria-hidden="true">»</div>

			<section class="chain-panel downstream-panel">
				<div class="panel-title"><div>下游终端消费链路</div></div>
				<div class="panel-body function-body middle-body">
					<!-- 节点 tabs -->
					<div class="node-tabs">
						<div class="node-tab" v-for="item in marketNodes" :key="item.name"
							:class="{ active: selectedDownstreamNode && selectedDownstreamNode.name === item.name }"
							@click="selectDownstreamNode(item)">
							<span class="tab-name">{{ item.name }}</span>
							<span class="tab-count">{{ item.count }}</span>
						</div>
					</div>

					<!-- 品牌 pills -->
					<div class="category-pills" v-if="selectedDownstreamNode">
						<div class="pill-label">品牌</div>
						<div class="pill-list">
							<div class="pill" v-for="b in selectedDownstreamNode.brands" :key="b.name"
								:class="{ active: selectedBrand && selectedBrand.name === b.name }"
								@click="selectBrand(b)">{{ b.name }}</div>
						</div>
					</div>

					<!-- 概览指标条 -->
					<div class="metric-strip" v-if="!selectedBrand">
						<div><b>活跃门店</b><strong>2468家</strong></div>
						<div><b>线上订单</b><strong>32万</strong></div>
						<div><b>海外链路</b><strong>18条</strong></div>
					</div>

					<!-- 选中品牌后的 5 段详情 -->
					<div class="middle-detail" v-if="selectedBrand">
						<!-- 品牌布局图谱 -->
						<div class="detail-section">
							<div class="section-head"><span>品牌布局图谱</span></div>
							<div class="brand-layout">
								<div class="layout-hq">
									<span class="layout-label">总部</span>
									<strong>{{ selectedBrand.layout.hq }}</strong>
								</div>
								<div class="layout-branches">
									<span class="layout-label">区域</span>
									<div class="branch-chips">
										<em v-for="r in selectedBrand.layout.regions" :key="r">{{ r }}</em>
									</div>
								</div>
								<div class="layout-stats">
									<div><b>直营</b><strong>{{ selectedBrand.layout.direct }}</strong></div>
									<div><b>加盟</b><strong>{{ selectedBrand.layout.franchise }}</strong></div>
									<div><b>海外</b><strong>{{ selectedBrand.layout.overseas }}</strong></div>
								</div>
							</div>
						</div>

						<!-- 门店密度热力 -->
						<div class="detail-section">
							<div class="section-head"><span>门店密度热力</span></div>
							<div class="bar-chart">
								<div class="bar-row" v-for="b in selectedBrand.density" :key="b.name">
									<span class="bar-label">{{ b.name }}</span>
									<div class="bar-track"><i :style="{ width: b.value + '%' }"></i></div>
									<span class="bar-val">{{ b.value }}</span>
								</div>
							</div>
						</div>

						<!-- 区域市场饱和度 -->
						<div class="detail-section">
							<div class="section-head"><span>区域市场饱和度</span></div>
							<div class="region-grid">
								<div class="region-cell" v-for="r in selectedBrand.saturation" :key="r.region" :class="r.status">
									<b>{{ r.region }}</b>
									<span>{{ r.value }}</span>
								</div>
							</div>
						</div>

						<!-- 消费流向 -->
						<div class="detail-section">
							<div class="section-head"><span>消费流向</span></div>
							<div class="flow-list">
								<div class="flow-row" v-for="f in selectedBrand.consumptionFlow" :key="f.to">
									<span class="flow-from">{{ f.to }}</span>
									<i class="flow-arrow"></i>
									<strong>{{ f.volume }}</strong>
								</div>
							</div>
						</div>

						<!-- 海外链路 -->
						<div class="detail-section">
							<div class="section-head"><span>海外链路</span></div>
							<div class="overseas-box">
								<div class="overseas-summary">
									<div><b>海外门店</b><strong>{{ selectedBrand.overseas.totalStores }}</strong></div>
									<div><b>趋势</b><strong>{{ selectedBrand.overseas.trend }}</strong></div>
								</div>
								<div class="overseas-routes" v-if="selectedBrand.overseas.routes.length">
									<div class="route-row" v-for="r in selectedBrand.overseas.routes" :key="r.country">
										<span class="route-country">{{ r.country }}</span>
										<span class="route-stores">{{ r.stores }}家</span>
										<span class="route-status" :class="r.status">{{ r.status }}</span>
									</div>
								</div>
								<p class="overseas-empty" v-else>暂无海外链路数据</p>
							</div>
						</div>
					</div>

					<!-- 未选中品牌时 -->
					<div class="detail-card" v-if="selectedDownstreamNode && !selectedBrand">
						<h3>{{ selectedDownstreamNode.name }}</h3>
						<p>{{ selectedDownstreamNode.desc }}</p>
					</div>
				</div>
			</section>
		</div>

		<section class="service-panel">
			<div class="panel-title"><div>配套服务</div></div>
			<div class="service-body">
				<div class="service-summary">
					<span class="summary-tag">服务配套</span>
					<strong>{{ selectedNode.name }}</strong>
					<span class="summary-desc">{{ selectedNode.desc }}</span>
					<div class="summary-metrics">
						<div><b>服务环节</b><strong>4</strong></div>
						<div><b>服务企业</b><strong>312</strong></div>
						<div><b>本月调度</b><strong>2.6万</strong></div>
					</div>
				</div>
				<div class="service-grid">
					<div class="service-card" v-for="item in serviceList" :key="item.title">
						<strong>{{ item.title }}</strong>
						<span>{{ item.desc }}</span>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
	import * as echarts from 'echarts'

	export default {
		name: 'IndustryChainMap',
		data() {
			return {
				selectedNode: { name: '蔬菜', desc: '查看蔬菜类原料基地分布、供应能力、采购热度与价格波动。' },
				selectedMiddleNode: null,
				selectedCategory: null,
				selectedDownstreamNode: null,
				selectedBrand: null,
				structureChartInstance: null,
				originGroups: [{
					title: '种植\n基地',
					categories: [{ name: '蔬菜', expanded: false, desc: '查看蔬菜类原料基地分布、供应能力、采购热度与价格波动。', children: ['辣椒', '生菜', '菠菜', '茼蒿', '白萝卜'] }, { name: '香辛料', expanded: true, desc: '查看花椒、八角等香辛料产地、等级、库存和溯源接入情况。', children: ['花椒', '八角', '桂皮'] }, { name: '菌类', expanded: true, desc: '查看菌类供应基地、采收周期、冷链需求和质检结果。', children: ['香菇', '金针菇'] }]
				}, {
					title: '养殖\n基地',
					categories: [{ name: '牛羊\n畜禽', expanded: true, desc: '查看肉类供应、检疫批次、价格趋势和门店采购匹配。', children: ['牛肉', '羊肉', '毛肚'] }, { name: '水产', expanded: true, desc: '查看水产原料来源、鲜活运输、损耗率和供应稳定性。', children: ['鱼片', '虾滑'] }]
				}],
				processNodes: [{
					name: '底料工厂', count: '42家', desc: '展示底料品类结构、产能分布、区域互补和外销流向。',
					categories: [
						{ name: '麻辣类',
							capacity: [{ name: '渝北', value: 32 }, { name: '江北', value: 24 }, { name: '南岸', value: 18 }, { name: '沙坪坝', value: 14 }, { name: '巴南', value: 12 }],
							structure: { total: '86吨/日', segments: [{ name: '牛油麻辣', value: 42, color: '#ff5f5f' }, { name: '清油麻辣', value: 28, color: '#ff8a3c' }, { name: '青花椒', value: 16, color: '#44c1ef' }, { name: '其他', value: 14, color: '#8ad4ff' }] },
							regionSupply: [{ region: '渝东北', value: '供应平衡', status: 'ok' }, { region: '渝东南', value: '缺口 12%', status: 'warn' }, { region: '渝西', value: '过剩 8%', status: 'warn' }, { region: '主城', value: '饱和', status: 'ok' }],
							exportFlow: [{ to: '成都', volume: '18吨/日' }, { to: '武汉', volume: '9吨/日' }, { to: '上海', volume: '6吨/日' }, { to: '海外', volume: '3吨/日' }],
							alert: { level: 'warn', levelLabel: '关注', message: '渝东南地区麻辣类底料供应缺口扩大，建议定向调配 5-8 吨。', surplus: '8%', gap: '12%', suggest: '跨区域调运' }
						},
						{ name: '清汤类',
							capacity: [{ name: '江北', value: 28 }, { name: '渝北', value: 22 }, { name: '南岸', value: 20 }, { name: '九龙坡', value: 16 }, { name: '其他', value: 14 }],
							structure: { total: '52吨/日', segments: [{ name: '大骨清汤', value: 38, color: '#44c1ef' }, { name: '菌汤', value: 26, color: '#8ad4ff' }, { name: '番茄清汤', value: 20, color: '#ff8a3c' }, { name: '其他', value: 16, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '缺口 6%', status: 'warn' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '成都', volume: '10吨/日' }, { to: '西安', volume: '6吨/日' }, { to: '北京', volume: '4吨/日' }],
							alert: { level: 'ok', levelLabel: '正常', message: '清汤类供需整体稳定，仅渝东北小幅缺口。', surplus: '3%', gap: '6%', suggest: '常规调度' }
						},
						{ name: '番茄类',
							capacity: [{ name: '渝北', value: 36 }, { name: '巴南', value: 22 }, { name: '南岸', value: 18 }, { name: '沙坪坝', value: 14 }, { name: '其他', value: 10 }],
							structure: { total: '38吨/日', segments: [{ name: '鲜番茄', value: 44, color: '#ff5f5f' }, { name: '番茄酱', value: 32, color: '#ff8a3c' }, { name: '番茄浓汤', value: 24, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '过剩 14%', status: 'warn' }, { region: '渝东北', value: '缺口 18%', status: 'danger' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '上海', volume: '8吨/日' }, { to: '广州', volume: '5吨/日' }, { to: '杭州', volume: '4吨/日' }],
							alert: { level: 'danger', levelLabel: '预警', message: '番茄类在渝东北缺口较大，同时渝西产能过剩 14%，建议跨区调度。', surplus: '14%', gap: '18%', suggest: '紧急调运' }
						},
						{ name: '酸汤类',
							capacity: [{ name: '巴南', value: 30 }, { name: '渝北', value: 24 }, { name: '南岸', value: 18 }, { name: '江北', value: 16 }, { name: '其他', value: 12 }],
							structure: { total: '24吨/日', segments: [{ name: '贵州酸汤', value: 40, color: '#f7ff57' }, { name: '川式酸汤', value: 35, color: '#44c1ef' }, { name: '番茄酸汤', value: 25, color: '#ff8a3c' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '平衡', status: 'ok' }, { region: '渝东南', value: '缺口 9%', status: 'warn' }],
							exportFlow: [{ to: '贵阳', volume: '7吨/日' }, { to: '成都', volume: '5吨/日' }, { to: '昆明', volume: '3吨/日' }],
							alert: { level: 'warn', levelLabel: '关注', message: '酸汤类整体平稳，渝东南小幅缺口。', surplus: '2%', gap: '9%', suggest: '常规调度' }
						}
					]
				}, {
					name: '生鲜食材加工厂', count: '36家', desc: '展示生鲜食材加工能力、品类结构、冷链半径与订单履约情况。',
					categories: [
						{ name: '毛肚类',
							capacity: [{ name: '渝北', value: 34 }, { name: '江北', value: 22 }, { name: '巴南', value: 20 }, { name: '南岸', value: 14 }, { name: '其他', value: 10 }],
							structure: { total: '62吨/日', segments: [{ name: '鲜毛肚', value: 52, color: '#ff5f5f' }, { name: '冷冻毛肚', value: 30, color: '#44c1ef' }, { name: '即食毛肚', value: 18, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '缺口 8%', status: 'warn' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '成都', volume: '12吨/日' }, { to: '武汉', volume: '6吨/日' }, { to: '西安', volume: '4吨/日' }],
							alert: { level: 'warn', levelLabel: '关注', message: '毛肚类冷链半径覆盖良好，渝东北小缺口。', surplus: '4%', gap: '8%', suggest: '常规调度' }
						},
						{ name: '牛肉类',
							capacity: [{ name: '江北', value: 30 }, { name: '渝北', value: 26 }, { name: '南岸', value: 18 }, { name: '沙坪坝', value: 14 }, { name: '其他', value: 12 }],
							structure: { total: '48吨/日', segments: [{ name: '鲜切牛肉', value: 46, color: '#ff5f5f' }, { name: '雪花牛肉', value: 28, color: '#ff8a3c' }, { name: '牛杂', value: 26, color: '#44c1ef' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '平衡', status: 'ok' }, { region: '渝东南', value: '缺口 10%', status: 'warn' }],
							exportFlow: [{ to: '上海', volume: '9吨/日' }, { to: '广州', volume: '6吨/日' }, { to: '深圳', volume: '4吨/日' }],
							alert: { level: 'ok', levelLabel: '正常', message: '牛肉类加工与供应整体平稳。', surplus: '2%', gap: '10%', suggest: '常规调度' }
						},
						{ name: '丸滑类',
							capacity: [{ name: '渝北', value: 32 }, { name: '巴南', value: 24 }, { name: '南岸', value: 18 }, { name: '江北', value: 14 }, { name: '其他', value: 12 }],
							structure: { total: '36吨/日', segments: [{ name: '虾滑', value: 38, color: '#44c1ef' }, { name: '牛肉丸', value: 30, color: '#ff5f5f' }, { name: '鱼丸', value: 18, color: '#8ad4ff' }, { name: '其他', value: 14, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '过剩 10%', status: 'warn' }, { region: '渝东北', value: '缺口 14%', status: 'danger' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '成都', volume: '8吨/日' }, { to: '武汉', volume: '5吨/日' }, { to: '长沙', volume: '3吨/日' }],
							alert: { level: 'danger', levelLabel: '预警', message: '丸滑类渝东北缺口较大，渝西产能过剩需调配。', surplus: '10%', gap: '14%', suggest: '紧急调运' }
						}
					]
				}, {
					name: '预制菜加工企业', count: '25家', desc: '展示预制菜品类结构、产能利用率、区域互补和缺口预警。',
					categories: [
						{ name: '自热火锅',
							capacity: [{ name: '渝北', value: 38 }, { name: '巴南', value: 22 }, { name: '南岸', value: 18 }, { name: '江北', value: 14 }, { name: '其他', value: 8 }],
							structure: { total: '12万盒/日', segments: [{ name: '麻辣味', value: 48, color: '#ff5f5f' }, { name: '番茄味', value: 26, color: '#ff8a3c' }, { name: '清汤味', value: 16, color: '#44c1ef' }, { name: '其他', value: 10, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '过剩 6%', status: 'warn' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '缺口 8%', status: 'warn' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '全国电商', volume: '8万盒/日' }, { to: '海外', volume: '1.2万盒/日' }],
							alert: { level: 'warn', levelLabel: '关注', message: '自热火锅旺季产能充足，淡季略过剩。', surplus: '6%', gap: '8%', suggest: '跨季度调度' }
						},
						{ name: '火锅食材包',
							capacity: [{ name: '江北', value: 32 }, { name: '渝北', value: 26 }, { name: '巴南', value: 18 }, { name: '南岸', value: 14 }, { name: '其他', value: 10 }],
							structure: { total: '4万套/日', segments: [{ name: '4人餐', value: 42, color: '#44c1ef' }, { name: '2人餐', value: 34, color: '#8ad4ff' }, { name: '家庭装', value: 24, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '平衡', status: 'ok' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '成都', volume: '1.5万套/日' }, { to: '上海', volume: '1万套/日' }],
							alert: { level: 'ok', levelLabel: '正常', message: '火锅食材包供需平稳。', surplus: '2%', gap: '3%', suggest: '常规调度' }
						}
					]
				}, {
					name: '包装厂', count: '18家', desc: '展示包装供应能力、环保材料占比和订单排期。',
					categories: [
						{ name: '底料包装',
							capacity: [{ name: '渝北', value: 36 }, { name: '巴南', value: 24 }, { name: '南岸', value: 18 }, { name: '江北', value: 14 }, { name: '其他', value: 8 }],
							structure: { total: '20万袋/日', segments: [{ name: '铝箔袋', value: 52, color: '#44c1ef' }, { name: '自立袋', value: 28, color: '#f7ff57' }, { name: '马口铁罐', value: 20, color: '#ff8a3c' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '缺口 6%', status: 'warn' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '本地底料厂', volume: '14万袋/日' }, { to: '周边地区', volume: '6万袋/日' }],
							alert: { level: 'ok', levelLabel: '正常', message: '底料包装供应稳定。', surplus: '3%', gap: '6%', suggest: '常规调度' }
						},
						{ name: '预制菜包装',
							capacity: [{ name: '巴南', value: 32 }, { name: '渝北', value: 26 }, { name: '南岸', value: 18 }, { name: '江北', value: 14 }, { name: '其他', value: 10 }],
							structure: { total: '15万盒/日', segments: [{ name: '自热盒', value: 44, color: '#ff5f5f' }, { name: 'PP餐盒', value: 34, color: '#44c1ef' }, { name: '纸质餐盒', value: 22, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '平衡', status: 'ok' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '本地预制菜厂', volume: '10万盒/日' }, { to: '电商渠道', volume: '5万盒/日' }],
							alert: { level: 'ok', levelLabel: '正常', message: '预制菜包装供应正常。', surplus: '2%', gap: '2%', suggest: '常规调度' }
						}
					]
				}, {
					name: '厨具设备厂', count: '15家', desc: '展示设备供应商、维保能力和门店采购需求。',
					categories: [
						{ name: '火锅灶具',
							capacity: [{ name: '渝北', value: 30 }, { name: '巴南', value: 26 }, { name: '南岸', value: 18 }, { name: '江北', value: 14 }, { name: '其他', value: 12 }],
							structure: { total: '1800台/月', segments: [{ name: '电磁炉', value: 48, color: '#44c1ef' }, { name: '燃气灶', value: 32, color: '#ff5f5f' }, { name: '电陶炉', value: 20, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '缺口 9%', status: 'warn' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '本地门店', volume: '1200台/月' }, { to: '周边地区', volume: '600台/月' }],
							alert: { level: 'warn', levelLabel: '关注', message: '火锅灶具采购旺季即将到来，建议提前备货。', surplus: '5%', gap: '9%', suggest: '提前备货' }
						},
						{ name: '制冷设备',
							capacity: [{ name: '江北', value: 34 }, { name: '渝北', value: 26 }, { name: '南岸', value: 18 }, { name: '巴南', value: 14 }, { name: '其他', value: 8 }],
							structure: { total: '900台/月', segments: [{ name: '冷藏柜', value: 44, color: '#44c1ef' }, { name: '冷冻柜', value: 34, color: '#ff5f5f' }, { name: '制冰机', value: 22, color: '#f7ff57' }] },
							regionSupply: [{ region: '主城', value: '平衡', status: 'ok' }, { region: '渝西', value: '平衡', status: 'ok' }, { region: '渝东北', value: '平衡', status: 'ok' }, { region: '渝东南', value: '平衡', status: 'ok' }],
							exportFlow: [{ to: '本地门店', volume: '600台/月' }, { to: '周边地区', volume: '300台/月' }],
							alert: { level: 'ok', levelLabel: '正常', message: '制冷设备供应稳定。', surplus: '3%', gap: '4%', suggest: '常规调度' }
						}
					]
				}],
				marketNodes: [
					{ name: '品牌总部', count: '86家', desc: '展示品牌布局图谱、直营/加盟结构和区域扩张能力。',
					brands: [
							{ name: '海底捞',
								layout: { hq: '海底捞国际控股', regions: ['西南总部', '华东总部', '华北总部', '华南总部'], direct: 920, franchise: 0, overseas: 68 },
								density: [{ name: '重庆', value: 88 }, { name: '成都', value: 72 }, { name: '北京', value: 65 }, { name: '上海', value: 58 }, { name: '广州', value: 42 }],
								saturation: [{ region: '重庆', value: '高密度', status: 'warn' }, { region: '成都', value: '高密度', status: 'warn' }, { region: '北京', value: '中密度', status: 'ok' }, { region: '上海', value: '中密度', status: 'ok' }],
								consumptionFlow: [{ to: '堂食', volume: '68%' }, { to: '外卖', volume: '22%' }, { to: '预制菜', volume: '10%' }],
								overseas: { routes: [{ country: '新加坡', stores: 12, status: '稳定' }, { country: '日本', stores: 8, status: '扩张中' }, { country: '美国', stores: 6, status: '稳定' }, { country: '韩国', stores: 5, status: '扩张中' }], totalStores: 68, trend: '年增 15%' }
							},
							{ name: '小龙坎',
								layout: { hq: '小龙坎餐饮管理', regions: ['西南中心', '华东中心', '华南中心'], direct: 120, franchise: 480, overseas: 12 },
								density: [{ name: '重庆', value: 72 }, { name: '成都', value: 68 }, { name: '武汉', value: 45 }, { name: '深圳', value: 38 }, { name: '西安', value: 28 }],
								saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '武汉', value: '低密度', status: 'ok' }, { region: '深圳', value: '低密度', status: 'ok' }],
								consumptionFlow: [{ to: '堂食', volume: '75%' }, { to: '外卖', volume: '18%' }, { to: '预制菜', volume: '7%' }],
								overseas: { routes: [{ country: '马来西亚', stores: 5, status: '稳定' }, { country: '泰国', stores: 4, status: '稳定' }, { country: '澳大利亚', stores: 3, status: '试运营' }], totalStores: 12, trend: '年增 20%' }
							},
							{ name: '蜀大侠',
								layout: { hq: '蜀大侠餐饮', regions: ['西南中心', '西北中心'], direct: 68, franchise: 320, overseas: 4 },
								density: [{ name: '重庆', value: 54 }, { name: '成都', value: 62 }, { name: '兰州', value: 28 }, { name: '乌鲁木齐', value: 18 }, { name: '昆明', value: 22 }],
								saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '兰州', value: '低密度', status: 'ok' }, { region: '昆明', value: '低密度', status: 'ok' }],
								consumptionFlow: [{ to: '堂食', volume: '80%' }, { to: '外卖', volume: '15%' }, { to: '预制菜', volume: '5%' }],
								overseas: { routes: [{ country: '迪拜', stores: 2, status: '试运营' }, { country: '泰国', stores: 2, status: '稳定' }], totalStores: 4, trend: '初步布局' }
							},
							{ name: '佩姐',
								layout: { hq: '佩姐老火锅', regions: ['西南总部', '华东总部'], direct: 45, franchise: 85, overseas: 2 },
								density: [{ name: '重庆', value: 65 }, { name: '上海', value: 42 }, { name: '杭州', value: 28 }, { name: '南京', value: 22 }, { name: '苏州', value: 18 }],
								saturation: [{ region: '重庆', value: '高密度', status: 'warn' }, { region: '上海', value: '中密度', status: 'ok' }, { region: '杭州', value: '低密度', status: 'ok' }, { region: '南京', value: '低密度', status: 'ok' }],
								consumptionFlow: [{ to: '堂食', volume: '85%' }, { to: '外卖', volume: '12%' }, { to: '预制菜', volume: '3%' }],
								overseas: { routes: [{ country: '加拿大', stores: 1, status: '试运营' }, { country: '澳大利亚', stores: 1, status: '试运营' }], totalStores: 2, trend: '探索阶段' }
							},
							{ name: '周师兄',
								layout: { hq: '周师兄火锅', regions: ['西南中心'], direct: 38, franchise: 142, overseas: 0 },
								density: [{ name: '重庆', value: 58 }, { name: '成都', value: 32 }, { name: '贵阳', value: 22 }, { name: '南宁', value: 15 }, { name: '长沙', value: 12 }],
								saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '低密度', status: 'ok' }, { region: '贵阳', value: '低密度', status: 'ok' }, { region: '南宁', value: '低密度', status: 'ok' }],
								consumptionFlow: [{ to: '堂食', volume: '82%' }, { to: '外卖', volume: '16%' }, { to: '预制菜', volume: '2%' }],
								overseas: { routes: [], totalStores: 0, trend: '暂无海外布局' }
							}
						]
					},
					{ name: '区域分公司', count: '136家', desc: '展示区域管理覆盖、门店密度和市场饱和度。',
						brands: [
							{ name: '海底捞西南', layout: { hq: '西南分公司（成都）', regions: ['重庆', '四川', '贵州', '云南'], direct: 280, franchise: 0, overseas: 0 }, density: [{ name: '重庆', value: 88 }, { name: '成都', value: 72 }, { name: '贵阳', value: 35 }, { name: '昆明', value: 28 }, { name: '遵义', value: 18 }], saturation: [{ region: '重庆', value: '高密度', status: 'warn' }, { region: '成都', value: '高密度', status: 'warn' }, { region: '贵阳', value: '中密度', status: 'ok' }, { region: '昆明', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '本地消费', volume: '72%' }, { to: '游客消费', volume: '22%' }, { to: '外卖辐射', volume: '6%' }], overseas: { routes: [], totalStores: 0, trend: '区域管理无海外直管' } },
							{ name: '小龙坎华东', layout: { hq: '华东分公司（上海）', regions: ['上海', '江苏', '浙江', '安徽'], direct: 48, franchise: 180, overseas: 0 }, density: [{ name: '上海', value: 58 }, { name: '杭州', value: 42 }, { name: '南京', value: 38 }, { name: '苏州', value: 28 }, { name: '合肥', value: 18 }], saturation: [{ region: '上海', value: '中密度', status: 'ok' }, { region: '杭州', value: '中密度', status: 'ok' }, { region: '南京', value: '低密度', status: 'ok' }, { region: '合肥', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '本地消费', volume: '68%' }, { to: '游客消费', volume: '25%' }, { to: '外卖辐射', volume: '7%' }], overseas: { routes: [], totalStores: 0, trend: '区域管理无海外直管' } },
							{ name: '蜀大侠西北', layout: { hq: '西北分公司（西安）', regions: ['陕西', '甘肃', '宁夏', '新疆'], direct: 28, franchise: 120, overseas: 0 }, density: [{ name: '西安', value: 45 }, { name: '兰州', value: 28 }, { name: '乌鲁木齐', value: 18 }, { name: '银川', value: 12 }, { name: '西宁', value: 8 }], saturation: [{ region: '西安', value: '中密度', status: 'ok' }, { region: '兰州', value: '低密度', status: 'ok' }, { region: '乌鲁木齐', value: '低密度', status: 'ok' }, { region: '银川', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '本地消费', volume: '78%' }, { to: '游客消费', volume: '15%' }, { to: '外卖辐射', volume: '7%' }], overseas: { routes: [], totalStores: 0, trend: '区域管理无海外直管' } }
						]
					},
					{ name: '直营店', count: '920家', desc: '展示直营门店经营数据、采购流向和人效指标。',
						brands: [
							{ name: '海底捞直营', layout: { hq: '直营体系', regions: ['一线15城', '新一线22城', '二线18城'], direct: 920, franchise: 0, overseas: 68 }, density: [{ name: '北京', value: 65 }, { name: '上海', value: 58 }, { name: '重庆', value: 88 }, { name: '成都', value: 72 }, { name: '深圳', value: 42 }], saturation: [{ region: '重庆', value: '高密度', status: 'warn' }, { region: '成都', value: '高密度', status: 'warn' }, { region: '北京', value: '中密度', status: 'ok' }, { region: '上海', value: '中密度', status: 'ok' }], consumptionFlow: [{ to: '堂食', volume: '68%' }, { to: '外卖', volume: '22%' }, { to: '零售', volume: '10%' }], overseas: { routes: [{ country: '新加坡', stores: 12, status: '稳定' }, { country: '日本', stores: 8, status: '扩张中' }], totalStores: 68, trend: '年增 12%' } },
							{ name: '小龙坎直营', layout: { hq: '直营体系', regions: ['一线8城', '新一线12城', '二线10城'], direct: 120, franchise: 0, overseas: 12 }, density: [{ name: '重庆', value: 72 }, { name: '成都', value: 68 }, { name: '武汉', value: 45 }, { name: '深圳', value: 38 }, { name: '西安', value: 28 }], saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '武汉', value: '低密度', status: 'ok' }, { region: '深圳', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '堂食', volume: '75%' }, { to: '外卖', volume: '18%' }, { to: '零售', volume: '7%' }], overseas: { routes: [{ country: '马来西亚', stores: 5, status: '稳定' }, { country: '泰国', stores: 4, status: '稳定' }], totalStores: 12, trend: '年增 18%' } },
							{ name: '佩姐直营', layout: { hq: '直营体系', regions: ['一线5城', '新一线8城'], direct: 45, franchise: 0, overseas: 2 }, density: [{ name: '重庆', value: 65 }, { name: '上海', value: 42 }, { name: '杭州', value: 28 }, { name: '南京', value: 22 }, { name: '苏州', value: 18 }], saturation: [{ region: '重庆', value: '高密度', status: 'warn' }, { region: '上海', value: '中密度', status: 'ok' }, { region: '杭州', value: '低密度', status: 'ok' }, { region: '南京', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '堂食', volume: '85%' }, { to: '外卖', volume: '12%' }, { to: '零售', volume: '3%' }], overseas: { routes: [{ country: '加拿大', stores: 1, status: '试运营' }], totalStores: 2, trend: '探索阶段' } }
						]
					},
					{ name: '加盟店', count: '1548家', desc: '展示加盟网络热力、合规检查和经营波动。',
						brands: [
							{ name: '小龙坎加盟', layout: { hq: '加盟管理中心', regions: ['华东', '西南', '华南', '华中'], direct: 0, franchise: 480, overseas: 0 }, density: [{ name: '重庆', value: 48 }, { name: '成都', value: 42 }, { name: '武汉', value: 38 }, { name: '深圳', value: 32 }, { name: '长沙', value: 25 }], saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '武汉', value: '中密度', status: 'ok' }, { region: '深圳', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '堂食', volume: '78%' }, { to: '外卖', volume: '17%' }, { to: '预制菜', volume: '5%' }], overseas: { routes: [], totalStores: 0, trend: '加盟仅限国内' } },
							{ name: '蜀大侠加盟', layout: { hq: '加盟管理中心', regions: ['西南', '西北', '华中'], direct: 0, franchise: 320, overseas: 0 }, density: [{ name: '重庆', value: 38 }, { name: '成都', value: 42 }, { name: '兰州', value: 28 }, { name: '武汉', value: 22 }, { name: '昆明', value: 18 }], saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '兰州', value: '低密度', status: 'ok' }, { region: '武汉', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '堂食', volume: '82%' }, { to: '外卖', volume: '14%' }, { to: '预制菜', volume: '4%' }], overseas: { routes: [], totalStores: 0, trend: '加盟仅限国内' } },
							{ name: '周师兄加盟', layout: { hq: '加盟管理中心', regions: ['西南', '华南'], direct: 0, franchise: 142, overseas: 0 }, density: [{ name: '重庆', value: 42 }, { name: '成都', value: 28 }, { name: '贵阳', value: 22 }, { name: '南宁', value: 15 }, { name: '长沙', value: 12 }], saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '低密度', status: 'ok' }, { region: '贵阳', value: '低密度', status: 'ok' }, { region: '南宁', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '堂食', volume: '84%' }, { to: '外卖', volume: '14%' }, { to: '预制菜', volume: '2%' }], overseas: { routes: [], totalStores: 0, trend: '加盟仅限国内' } }
						]
					},
					{ name: '线上外卖', count: '32万单', desc: '展示线上订单热力、客单价、复购和消费流向。',
						brands: [
							{ name: '海底捞外卖', layout: { hq: '外卖运营中心', regions: ['美团', '饿了么', '小程序'], direct: 920, franchise: 0, overseas: 68 }, density: [{ name: '重庆', value: 82 }, { name: '成都', value: 68 }, { name: '北京', value: 58 }, { name: '上海', value: 52 }, { name: '深圳', value: 38 }], saturation: [{ region: '重庆', value: '高密度', status: 'warn' }, { region: '成都', value: '高密度', status: 'warn' }, { region: '北京', value: '中密度', status: 'ok' }, { region: '上海', value: '中密度', status: 'ok' }], consumptionFlow: [{ to: '美团', volume: '48%' }, { to: '饿了么', volume: '32%' }, { to: '小程序', volume: '20%' }], overseas: { routes: [{ country: '新加坡', stores: 12, status: '稳定' }, { country: '日本', stores: 8, status: '扩张中' }], totalStores: 68, trend: '外卖年增 25%' } },
							{ name: '小龙坎外卖', layout: { hq: '外卖运营中心', regions: ['美团', '饿了么', '抖音'], direct: 120, franchise: 480, overseas: 12 }, density: [{ name: '重庆', value: 68 }, { name: '成都', value: 62 }, { name: '武汉', value: 42 }, { name: '深圳', value: 35 }, { name: '西安', value: 25 }], saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '武汉', value: '低密度', status: 'ok' }, { region: '深圳', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '美团', volume: '52%' }, { to: '饿了么', volume: '30%' }, { to: '抖音', volume: '18%' }], overseas: { routes: [{ country: '马来西亚', stores: 5, status: '稳定' }], totalStores: 12, trend: '外卖年增 30%' } },
							{ name: '蜀大侠外卖', layout: { hq: '外卖运营中心', regions: ['美团', '饿了么'], direct: 68, franchise: 320, overseas: 4 }, density: [{ name: '重庆', value: 48 }, { name: '成都', value: 52 }, { name: '兰州', value: 22 }, { name: '武汉', value: 18 }, { name: '昆明', value: 15 }], saturation: [{ region: '重庆', value: '中密度', status: 'ok' }, { region: '成都', value: '中密度', status: 'ok' }, { region: '兰州', value: '低密度', status: 'ok' }, { region: '昆明', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '美团', volume: '55%' }, { to: '饿了么', volume: '35%' }, { to: '抖音', volume: '10%' }], overseas: { routes: [{ country: '泰国', stores: 2, status: '稳定' }], totalStores: 4, trend: '外卖年增 20%' } }
						]
					},
					{ name: '海外门店', count: '68家', desc: '展示海外链路、供应路径和品牌出海趋势。',
						brands: [
							{ name: '海底捞海外', layout: { hq: '海外事业部（新加坡）', regions: ['东南亚', '东亚', '北美'], direct: 68, franchise: 0, overseas: 68 }, density: [{ name: '新加坡', value: 82 }, { name: '日本', value: 68 }, { name: '美国', value: 52 }, { name: '韩国', value: 45 }, { name: '澳洲', value: 28 }], saturation: [{ region: '新加坡', value: '高密度', status: 'warn' }, { region: '日本', value: '中密度', status: 'ok' }, { region: '美国', value: '中密度', status: 'ok' }, { region: '韩国', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '华人消费', volume: '52%' }, { to: '本地消费', volume: '38%' }, { to: '游客消费', volume: '10%' }], overseas: { routes: [{ country: '新加坡', stores: 12, status: '稳定' }, { country: '日本', stores: 8, status: '扩张中' }, { country: '美国', stores: 6, status: '稳定' }, { country: '韩国', stores: 5, status: '扩张中' }, { country: '澳洲', stores: 4, status: '稳定' }], totalStores: 68, trend: '年增 15%，重点拓展日韩' } },
							{ name: '小龙坎海外', layout: { hq: '海外事业部', regions: ['东南亚', '澳洲'], direct: 12, franchise: 0, overseas: 12 }, density: [{ name: '马来西亚', value: 62 }, { name: '泰国', value: 55 }, { name: '澳洲', value: 32 }, { name: '印尼', value: 18 }, { name: '越南', value: 12 }], saturation: [{ region: '马来西亚', value: '中密度', status: 'ok' }, { region: '泰国', value: '中密度', status: 'ok' }, { region: '澳洲', value: '低密度', status: 'ok' }, { region: '印尼', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '华人消费', volume: '62%' }, { to: '本地消费', volume: '30%' }, { to: '游客消费', volume: '8%' }], overseas: { routes: [{ country: '马来西亚', stores: 5, status: '稳定' }, { country: '泰国', stores: 4, status: '稳定' }, { country: '澳洲', stores: 3, status: '试运营' }], totalStores: 12, trend: '年增 20%，深耕东南亚' } },
							{ name: '蜀大侠海外', layout: { hq: '海外试运营', regions: ['中东', '东南亚'], direct: 4, franchise: 0, overseas: 4 }, density: [{ name: '迪拜', value: 42 }, { name: '泰国', value: 38 }, { name: '阿布扎比', value: 22 }, { name: '吉隆坡', value: 15 }, { name: '曼谷', value: 12 }], saturation: [{ region: '迪拜', value: '低密度', status: 'ok' }, { region: '泰国', value: '低密度', status: 'ok' }, { region: '阿布扎比', value: '低密度', status: 'ok' }, { region: '吉隆坡', value: '低密度', status: 'ok' }], consumptionFlow: [{ to: '华人消费', volume: '45%' }, { to: '本地消费', volume: '48%' }, { to: '游客消费', volume: '7%' }], overseas: { routes: [{ country: '迪拜', stores: 2, status: '试运营' }, { country: '泰国', stores: 2, status: '稳定' }], totalStores: 4, trend: '初步布局中东' } }
						]
					}
				],
				serviceList: [{ title: '物流仓储', desc: '冷链监控、前置仓管理、配送路径优化' }, { title: '金融服务', desc: '企业信用画像、供应链金融产品申请' }, { title: '人才服务', desc: '火锅人才库、技能认证、人岗匹配' }, { title: '质检溯源', desc: '全链条抽检、批次溯源、风险预警' }]
			}
		},
		computed: {
			middleSummary() {
				return this.selectedMiddleNode ? { title: this.selectedMiddleNode.name, desc: this.selectedMiddleNode.desc } : { title: '生产加工能力', desc: '按节点查看各品类产能分布、品类结构、区域互补、外销流向与预警情况。' }
			},
			downstreamSummary() {
				return this.selectedNode.name && this.marketNodes.some(item => item.name === this.selectedNode.name) ? { title: this.selectedNode.name, desc: this.selectedNode.desc } : { title: '终端消费链路', desc: '按品牌、门店和渠道查看消费热力、市场饱和度、采购流向和海外链路。' }
			}
		},
		created() {
			this.selectedMiddleNode = this.processNodes[0]
			this.selectedCategory = this.processNodes[0].categories[0]
			this.selectedDownstreamNode = this.marketNodes[0]
			this.selectedBrand = this.marketNodes[0].brands[0]
		},
		watch: {
			selectedCategory: {
				handler() { this.$nextTick(this.renderStructureChart) },
				immediate: true
			}
		},
		beforeDestroy() {
			if (this.structureChartInstance) {
				this.structureChartInstance.dispose()
				this.structureChartInstance = null
			}
		},
		methods: {
			renderStructureChart() {
				if (!this.selectedCategory || !this.$refs.structureChart) return
				if (!this.structureChartInstance) {
					this.structureChartInstance = echarts.init(this.$refs.structureChart)
				}
				const segs = this.selectedCategory.structure.segments
				const option = {
					tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
					legend: {
						orient: 'vertical',
						right: 80,
						top: 'center',
						itemWidth: 10,
						itemHeight: 10,
						itemGap: 6,
						textStyle: { color: '#b8e4f8', fontSize: 14 },
						data: segs.map(s => s.name)
					},
					series: [{
						type: 'pie',
						radius: ['52%', '88%'],
						center: ['38%', '50%'],
						avoidLabelOverlap: true,
						itemStyle: { borderColor: 'rgba(6,50,100,0.8)', borderWidth: 2 },
						label: { show: false },
						emphasis: {
							scale: true, scaleSize: 6,
							itemStyle: { shadowBlur: 14, shadowColor: 'rgba(68,193,239,0.5)' },
							label: { show: false }
						},
						labelLine: { show: false },
						data: segs.map(s => ({ name: s.name, value: s.value, itemStyle: { color: s.color } }))
					}]
				}
				this.structureChartInstance.setOption(option, true)
			},
			selectNode(stage, item) {
				this.selectedNode = item
				if (stage !== 'middle') {
					this.selectedMiddleNode = null
					this.selectedCategory = null
				}
			},
			selectChild(child, parent) {
				this.selectedNode = { name: child, desc: parent.desc + '（当前关注：' + child + '）' }
			},
			selectMiddleNode(item) {
				this.selectedMiddleNode = item
				this.selectedCategory = item.categories && item.categories.length ? item.categories[0] : null
				this.selectedNode = item
			},
			selectCategory(cat) {
				this.selectedCategory = cat
			},
			selectDownstreamNode(item) {
				this.selectedDownstreamNode = item
				this.selectedBrand = item.brands && item.brands.length ? item.brands[0] : null
			},
			selectBrand(brand) {
				this.selectedBrand = brand
			},
			isNodeSelected(stage) {
				if (stage === 'middle') return !!this.selectedMiddleNode
				if (stage === 'downstream') return this.marketNodes.some(item => item.name === this.selectedNode.name)
				return false
			}
		}
	}
</script>

<style scoped lang="scss">
	.industry-chain-page {
		position: absolute;
		top: 102px;
		right: 0;
		bottom: 0;
		left: 0;
		display: grid;
		grid-template-rows: minmax(0, 1fr) 190px;
		row-gap: 16px;
		box-sizing: border-box;
		padding: 10px 68px 28px;
		overflow: hidden;
		color: #d8f1ff;
		font-family: 'Microsoft YaHei', sans-serif;
	}

	.chain-grid {
		display: grid;
		grid-template-columns: minmax(320px, 1.04fr) 52px minmax(360px, 1fr) 52px minmax(360px, 1fr);
		gap: 18px;
		height: 100%;
		min-height: 0;
		overflow: hidden;
	}

	.chain-panel,
	.service-panel {
		position: relative;
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 0;
		transition: filter .2s;
	}

	.panel-title {
		flex: 0 0 auto;
		width: 100%;
		height: 42px;
		background: url('https://xlqy.obs.cn-southwest-2.myhuaweicloud.com/screen/%E6%A0%87%E9%A2%98%402x.png') center / 100% 100% no-repeat;
	}

	.panel-title div {
		padding-left: 52px;
		font-family: JiangChengXieHei, JiangChengXieHei;
		font-size: 24px;
		font-weight: 900;
		font-style: italic;
		line-height: 34px;
		background: linear-gradient(180deg, #fff 15%, #f7ff57 81%);
		-webkit-background-clip: text;
		-webkit-text-fill-color: transparent;
	}

	.panel-body,
	.service-body {
		position: relative;
		flex: 1 1 auto;
		min-height: 0;
		box-sizing: border-box;
		height: auto;
		padding: 22px 24px;
		border: 1px solid rgba(68, 193, 239, .42);
		border-radius: 4px;
		background:
			linear-gradient(135deg, rgba(68, 193, 239, .14), transparent 22%) top left,
			linear-gradient(315deg, rgba(68, 193, 239, .1), transparent 22%) bottom right,
			rgba(10, 63, 113, .34);
		box-shadow: inset 0 0 24px rgba(24, 132, 255, .12), 0 0 10px rgba(0, 14, 40, .22);
		overflow: auto;
	}

	.panel-body::-webkit-scrollbar,
	.service-body::-webkit-scrollbar { width: 6px; height: 6px; }
	.panel-body::-webkit-scrollbar-track,
	.service-body::-webkit-scrollbar-track { background: rgba(202, 199, 199, .08); border-radius: 4px; }
	.panel-body::-webkit-scrollbar-thumb,
	.service-body::-webkit-scrollbar-thumb { background: rgba(68, 193, 239, .55); border-radius: 4px; }

	.panel-body:before,
	.service-body:before {
		content: '';
		position: absolute;
		top: -1px;
		left: -1px;
		width: 32px;
		height: 11px;
		border-top: 2px solid #44c1ef;
		border-left: 2px solid #44c1ef;
	}

	/* 上游种养 */
	.upstream-body { display: grid; grid-template-rows: 1fr 1fr; gap: 12px; padding: 18px 20px; align-content: start; }
	.origin-group { display: grid; grid-template-columns: 88px 1fr; align-items: stretch; gap: 12px; min-height: 0; }
	.source-node,
	.category-node span { display: flex; align-items: center; justify-content: center; white-space: pre-line; text-align: center; }
	.source-node {
		width: 100%; height: 100%; min-height: 82px; border: 1px solid rgba(68, 193, 239, .48);
		border-radius: 4px; color: #fff; font-size: 18px; line-height: 1.45;
		background: linear-gradient(180deg, rgba(17, 126, 186, .5), rgba(8, 45, 91, .52));
		box-shadow: 0 0 18px rgba(68, 193, 239, .18);
	}
	.chain-list { display: grid; grid-template-columns: 1fr; gap: 8px; min-width: 0; }
	.category-node { position: relative; display: grid; grid-template-columns: 80px 20px minmax(0, 1fr); align-items: center; gap: 8px; min-height: 38px; padding: 0; cursor: pointer; }
	.category-node.selected span,
	.category-node:hover span { border-color: #f7ff57; color: #f7ff57; box-shadow: 0 0 16px rgba(247, 255, 87, .22); }
	.category-node span { width: 80px; height: 34px; border: 1px solid rgba(216, 241, 255, .58); border-radius: 3px; color: #f6f9fe; font-size: 14px; line-height: 1.35; background: rgba(9, 60, 114, .62); }
	.category-node i { position: relative; display: block; height: 1px; background: rgba(216, 241, 255, .55); }
	.category-node i:after { content: ''; position: absolute; right: -1px; top: -4px; width: 0; height: 0; border-top: 4px solid transparent; border-bottom: 4px solid transparent; border-left: 8px solid rgba(216, 241, 255, .72); }
	.sub-list { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; margin-top: 0; }
	.sub-list em { min-width: 48px; padding: 4px 6px; border: 1px solid rgba(68, 193, 239, .48); border-radius: 3px; color: #b8e4f8; font-size: 12px; font-style: normal; text-align: center; background: rgba(8, 55, 106, .72); cursor: pointer; transition: .2s; }
	.sub-list em:hover { color: #fff; border-color: #44c1ef; background: rgba(23, 137, 213, .35); }
	.sub-list em.active { border-color: #f7ff57; color: #f7ff57; background: rgba(247, 255, 87, .12); box-shadow: 0 0 10px rgba(247, 255, 87, .25); }

	/* 双箭头 » */
	.chain-arrow { position: relative; display: flex; align-items: center; justify-content: center; padding-top: 42px; font-size: 48px; font-weight: 900; color: #44c1ef; text-shadow: 0 0 12px rgba(68, 193, 239, .6); line-height: 1; }

	/* 中下游通用 body */
	.function-body { display: flex; flex-direction: column; gap: 14px; padding: 20px 24px; }

	/* 节点 tabs（均分不滚动） */
	.node-tabs { display: flex; flex-wrap: nowrap; gap: 6px; overflow: hidden; }
	.node-tab {
		display: inline-flex; flex-direction: column; align-items: center; justify-content: center;
		flex: 1 1 0; min-width: 0; padding: 6px 4px;
		border: 1px solid rgba(68, 193, 239, .36); border-radius: 4px;
		color: #b8e4f8; font-size: 12px; cursor: pointer;
		background: rgba(6, 50, 100, .52); transition: .2s;
	}
	.node-tab:hover { border-color: #44c1ef; color: #fff; }
	.node-tab.active { border-color: #f7ff57; color: #f7ff57; background: rgba(18, 104, 166, .68); box-shadow: inset 0 0 14px rgba(247, 255, 87, .1); }
	.node-tab .tab-name { font-size: 12px; font-weight: 500; margin-bottom: 2px; white-space: normal; text-align: center; line-height: 1.2; }
	.node-tab .tab-count { font-family: DINPro, DIN, sans-serif; font-size: 13px; }

	.metric-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
	.metric-strip div { padding: 12px 8px; border-bottom: 1px solid rgba(68, 193, 239, .45); background: linear-gradient(180deg, rgba(10, 125, 190, .13), rgba(10, 125, 190, 0)); text-align: center; }
	.metric-strip b { display: block; margin-bottom: 8px; color: #8ad4ff; font-size: 13px; font-weight: 400; }
	.metric-strip strong { font-family: DINPro, DIN, sans-serif; font-size: 24px; font-weight: 600; background: linear-gradient(180deg, #fff 15%, #f7ff57 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }

	/* 详情卡 */
	.detail-card { position: relative; flex: 1; min-height: 0; padding: 18px 20px; border: 1px solid rgba(247, 255, 87, .25); background: rgba(247, 255, 87, .05); transition: .2s; }
	.detail-card.is-active { border-color: rgba(247, 255, 87, .6); background: linear-gradient(180deg, rgba(247, 255, 87, .12), rgba(247, 255, 87, .03)); box-shadow: inset 0 0 22px rgba(247, 255, 87, .1); }
	.detail-card.empty-card { border-color: rgba(68, 193, 239, .3); background: rgba(10, 63, 113, .2); }
	.copy-block h3,
	.detail-card h3 { margin: 0 0 12px; color: #8ad4ff; font-size: 22px; font-weight: 500; }
	.detail-card.is-active h3 { color: #f7ff57; }
	.copy-block p,
	.detail-card p { margin: 0; color: #d8f1ff; font-size: 20px; line-height: 1.8; }

	/* ============ 中游详情 ============ */
	.middle-body { padding: 20px; }

	.category-pills { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
	.pill-label { color: #8ad4ff; font-size: 13px; flex: 0 0 auto; }
	.pill-list { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
	.pill { padding: 5px 12px; border: 1px solid rgba(68, 193, 239, .45); border-radius: 14px; color: #b8e4f8; font-size: 12px; background: rgba(6, 50, 100, .45); cursor: pointer; transition: .2s; }
	.pill:hover { color: #fff; border-color: #44c1ef; }
	.pill.active { color: #f7ff57; border-color: #f7ff57; background: rgba(247, 255, 87, .12); box-shadow: 0 0 10px rgba(247, 255, 87, .25); }

	/* 滚动条显示出来 */
	.middle-detail { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; padding-right: 4px; }
	.middle-detail::-webkit-scrollbar { width: 6px; }
	.middle-detail::-webkit-scrollbar-track { background: rgba(202, 199, 199, .08); border-radius: 4px; }
	.middle-detail::-webkit-scrollbar-thumb { background: rgba(68, 193, 239, .55); border-radius: 4px; }

	.detail-section { padding: 12px 14px; border: 1px solid rgba(68, 193, 239, .28); border-radius: 3px; background: rgba(6, 50, 100, .35); }
	.section-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; color: #d8f1ff; font-size: 14px; font-weight: 500; }
	.section-head .level-badge { margin-left: auto; padding: 2px 10px; border-radius: 10px; font-size: 11px; font-weight: 500; }
	.level-badge.ok { color: #8ad4ff; border: 1px solid rgba(68, 193, 239, .55); background: rgba(68, 193, 239, .1); }
	.level-badge.warn { color: #f7ff57; border: 1px solid rgba(247, 255, 87, .55); background: rgba(247, 255, 87, .1); }
	.level-badge.danger { color: #ff8585; border: 1px solid rgba(255, 95, 95, .6); background: rgba(255, 95, 95, .12); }

	/* 产能分布：条形图 */
	.bar-chart { display: flex; flex-direction: column; gap: 6px; }
	.bar-row { display: grid; grid-template-columns: 52px 1fr 38px; align-items: center; gap: 8px; font-size: 12px; }
	.bar-label { color: #8ad4ff; text-align: right; }
	.bar-track { height: 10px; border-radius: 5px; background: rgba(68, 193, 239, .14); overflow: hidden; }
	.bar-track i { display: block; height: 100%; border-radius: 5px; background: linear-gradient(90deg, #168cff, #44c1ef); box-shadow: 0 0 6px rgba(68, 193, 239, .5); }
	.bar-val { color: #f7ff57; font-family: DINPro, DIN, sans-serif; text-align: right; }

	/* ECharts 饼图容器 */
	.structure-chart { width: 100%; height: 180px; }

	/* 区域产能互补 */
	.region-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
	.region-cell { padding: 8px 6px; text-align: center; border: 1px solid rgba(68, 193, 239, .35); border-radius: 3px; background: rgba(6, 50, 100, .4); }
	.region-cell b { display: block; color: #d8f1ff; font-size: 12px; font-weight: 500; margin-bottom: 4px; }
	.region-cell span { font-size: 11px; }
	.region-cell.ok { border-color: rgba(68, 193, 239, .5); }
	.region-cell.ok span { color: #8ad4ff; }
	.region-cell.warn { border-color: rgba(247, 255, 87, .55); background: rgba(247, 255, 87, .08); }
	.region-cell.warn span { color: #f7ff57; }
	.region-cell.danger { border-color: rgba(255, 95, 95, .6); background: rgba(255, 95, 95, .12); }
	.region-cell.danger span { color: #ff8585; }

	/* 产品外销流向 */
	.flow-list { display: flex; flex-direction: column; gap: 6px; }
	.flow-row { display: grid; grid-template-columns: 50px 36px 1fr auto; align-items: center; gap: 8px; font-size: 12px; }
	.flow-from { color: #8ad4ff; text-align: right; }
	.flow-arrow { position: relative; display: block; height: 1px; background: rgba(68, 193, 239, .55); }
	.flow-arrow:after { content: ''; position: absolute; right: -1px; top: -3px; width: 0; height: 0; border-top: 3px solid transparent; border-bottom: 3px solid transparent; border-left: 6px solid rgba(68, 193, 239, .8); }
	.flow-to { color: #d8f1ff; }
	.flow-row strong { color: #f7ff57; font-family: DINPro, DIN, sans-serif; font-size: 13px; }

	/* 产能过剩/缺口预警 */
	.alert-section { border-color: rgba(68, 193, 239, .35); }
	.alert-section.warn { border-color: rgba(247, 255, 87, .5); background: linear-gradient(180deg, rgba(247, 255, 87, .08), rgba(6, 50, 100, .35)); }
	.alert-section.danger { border-color: rgba(255, 95, 95, .55); background: linear-gradient(180deg, rgba(255, 95, 95, .12), rgba(6, 50, 100, .35)); }
	.alert-body p { margin: 0 0 10px; color: #d8f1ff; font-size: 13px; line-height: 1.6; }
	.alert-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
	.alert-metrics div { padding: 6px 4px; text-align: center; border-bottom: 1px solid rgba(68, 193, 239, .35); background: rgba(6, 50, 100, .35); }
	.alert-metrics b { display: block; margin-bottom: 4px; color: #8ad4ff; font-size: 11px; font-weight: 400; }
	.alert-metrics strong { font-family: DINPro, DIN, sans-serif; font-size: 14px; color: #f7ff57; }

		/* 品牌布局图谱 */
		.brand-layout { display: flex; flex-direction: column; gap: 8px; }
		.layout-hq, .layout-branches { display: flex; align-items: center; gap: 8px; padding: 4px 0; }
		.layout-label { flex: 0 0 36px; color: #8ad4ff; font-size: 11px; }
		.layout-hq strong { color: #f7ff57; font-size: 13px; }
		.branch-chips { display: flex; flex-wrap: wrap; gap: 4px; }
		.branch-chips em { display: inline-block; padding: 2px 8px; border: 1px solid rgba(68, 193, 239, .4); border-radius: 10px; color: #b8e4f8; font-size: 11px; font-style: normal; background: rgba(6, 50, 100, .5); }
		.layout-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 4px; }
		.layout-stats > div { padding: 6px 4px; text-align: center; border-bottom: 1px solid rgba(68, 193, 239, .35); background: rgba(6, 50, 100, .35); }
		.layout-stats b { display: block; margin-bottom: 4px; color: #8ad4ff; font-size: 11px; font-weight: 400; }
		.layout-stats strong { font-family: DINPro, DIN, sans-serif; font-size: 14px; color: #f7ff57; }

		/* 海外链路 */
		.overseas-box { display: flex; flex-direction: column; gap: 8px; }
		.overseas-summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
		.overseas-summary > div { padding: 6px 4px; text-align: center; border-bottom: 1px solid rgba(68, 193, 239, .35); background: rgba(6, 50, 100, .35); }
		.overseas-summary b { display: block; margin-bottom: 4px; color: #8ad4ff; font-size: 11px; font-weight: 400; }
		.overseas-summary strong { font-family: DINPro, DIN, sans-serif; font-size: 14px; color: #f7ff57; }
		.overseas-routes { display: flex; flex-direction: column; gap: 4px; }
		.route-row { display: flex; align-items: center; gap: 8px; padding: 4px 8px; border: 1px solid rgba(68, 193, 239, .3); border-radius: 3px; background: rgba(6, 50, 100, .4); }
		.route-country { flex: 1; color: #d8f1ff; font-size: 12px; }
		.route-stores { color: #f7ff57; font-size: 12px; font-family: DINPro, DIN, sans-serif; }
		.route-status { padding: 1px 6px; border-radius: 8px; font-size: 10px; }
		.route-status.稳定 { color: #44c1ef; background: rgba(68, 193, 239, .15); }
		.route-status.扩张中 { color: #f7ff57; background: rgba(247, 255, 87, .15); }
		.route-status.试运营 { color: #ff8a3c; background: rgba(255, 138, 60, .15); }
		.overseas-empty { margin: 0; padding: 8px; text-align: center; color: #8ad4ff; font-size: 12px; }

		/* 配套服务 */
	.service-panel { height: 100%; margin-top: 0; }
	.service-body { display: grid; grid-template-columns: 390px 1fr; gap: 28px; padding: 24px 30px; }
	.service-summary { display: flex; flex-direction: column; justify-content: center; color: #f7ff57; font-size: 18px; line-height: 1.65; }
	.service-summary .summary-tag { display: inline-block; align-self: flex-start; margin-bottom: 8px; padding: 2px 10px; border: 1px solid rgba(68, 193, 239, .55); border-radius: 12px; color: #d8f1ff; font-size: 12px; background: rgba(6, 50, 100, .6); }
	.service-summary strong { margin-bottom: 8px; font-size: 26px; }
	.service-summary .summary-desc { margin-bottom: 12px; color: #b3c8d4; font-size: 13px; line-height: 1.55; }
	.summary-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 4px; }
	.summary-metrics div { padding: 10px 8px; border-bottom: 1px solid rgba(68, 193, 239, .45); background: linear-gradient(180deg, rgba(10, 125, 190, .13), rgba(10, 125, 190, 0)); text-align: center; }
	.summary-metrics b { display: block; margin-bottom: 6px; color: #8ad4ff; font-size: 12px; font-weight: 400; }
	.summary-metrics strong { font-family: DINPro, DIN, sans-serif; font-size: 20px; font-weight: 500; background: linear-gradient(180deg, #fff 15%, #f7ff57 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
	.service-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
	.service-card { padding: 18px 16px; border: 1px solid rgba(68, 193, 239, .36); background: rgba(6, 50, 100, .5); transition: .2s; }
	.service-card:hover { border-color: #44c1ef; background: rgba(18, 104, 166, .55); box-shadow: 0 0 14px rgba(68, 193, 239, .25); }
	.service-card strong { display: block; margin-bottom: 10px; color: #8ad4ff; font-size: 20px; font-weight: 500; }
	.service-card span { color: #b3c8d4; font-size: 14px; line-height: 1.55; }

	/* ============ 断点适配 ============ */
	@media (max-width: 1500px), (max-height: 820px) {
		.industry-chain-page { grid-template-rows: minmax(0, 1fr) 164px; row-gap: 10px; padding: 8px 38px 22px; }
		.chain-grid { grid-template-columns: minmax(290px, 1.06fr) 36px minmax(310px, 1fr) 36px minmax(310px, 1fr); gap: 10px; }
		.panel-title { height: 36px; }
		.panel-title div { padding-left: 44px; font-size: 20px; line-height: 30px; }
		.upstream-body { padding: 12px 14px; gap: 8px; }
		.origin-group { grid-template-columns: 72px 1fr; gap: 8px; }
		.source-node { min-height: 72px; font-size: 15px; }
		.category-node { grid-template-columns: 66px 16px minmax(0, 1fr); min-height: 32px; gap: 6px; }
		.category-node span { width: 66px; height: 28px; font-size: 12px; }
		.sub-list { gap: 4px; margin-top: 6px; }
		.sub-list em { min-width: 42px; padding: 3px 4px; font-size: 10px; }
		.function-body { padding: 14px 16px; gap: 10px; }
		.node-tab { padding: 5px 4px; }
		.node-tab .tab-name { font-size: 11px; }
		.node-tab .tab-count { font-size: 12px; }
		.metric-strip { gap: 7px; }
		.metric-strip div { padding: 8px 5px; }
		.metric-strip b { margin-bottom: 5px; font-size: 11px; }
		.metric-strip strong { font-size: 19px; }
		.detail-card { padding: 12px 14px; }
		.detail-card h3 { margin-bottom: 7px; font-size: 18px; }
		.detail-card p { font-size: 15px; line-height: 1.6; }
		.chain-arrow { padding-top: 36px; }
		/* 中游详情紧凑化 */
		.middle-body { padding: 16px; }
		.category-pills { gap: 8px; }
		.pill { padding: 4px 10px; font-size: 11px; }
		.detail-section { padding: 10px 12px; }
		.section-head { font-size: 13px; margin-bottom: 8px; }
		.bar-row { font-size: 11px; }
		.bar-track { height: 8px; }
		.bar-val { font-size: 11px; }
		.structure-chart { height: 150px; }
		.region-grid { grid-template-columns: repeat(2, 1fr); }
		.region-cell { padding: 6px 4px; }
		.region-cell b { font-size: 11px; margin-bottom: 2px; }
		.region-cell span { font-size: 10px; }
		.flow-row { font-size: 11px; }
		.alert-body p { font-size: 12px; margin-bottom: 8px; }
		.alert-metrics strong { font-size: 13px; }
		/* 配套服务 */
		.service-body { grid-template-columns: 320px 1fr; gap: 20px; padding: 18px 22px; }
		.service-summary strong { font-size: 20px; }
		.service-summary .summary-desc { font-size: 11px; margin-bottom: 8px; }
		.summary-metrics div { padding: 7px 4px; }
		.summary-metrics b { margin-bottom: 4px; font-size: 10px; }
		.summary-metrics strong { font-size: 15px; }
		.service-card { padding: 12px 12px; }
		.service-card strong { margin-bottom: 7px; font-size: 17px; }
		.service-card span { font-size: 12px; }
	}

	@media (max-width: 1200px) {
		.industry-chain-page { grid-template-rows: minmax(0, 1fr) 154px; row-gap: 10px; padding: 8px 24px 20px; }
		.chain-grid { grid-template-columns: minmax(260px, 1fr) minmax(280px, 1fr) minmax(280px, 1fr); gap: 10px; }
		.chain-arrow { display: none; }
		.upstream-body { padding: 12px; }
		.origin-group { grid-template-columns: 64px 1fr; }
		.source-node { min-height: 68px; font-size: 13px; }
		.category-node { grid-template-columns: 60px 14px minmax(0, 1fr); min-height: 30px; gap: 5px; }
		.category-node span { width: 60px; height: 26px; font-size: 12px; }
		.sub-list em { min-width: 34px; font-size: 9px; }
		.function-body { padding: 14px; gap: 10px; }
		.node-tab { padding: 4px 3px; }
		.node-tab .tab-name { font-size: 10px; }
		.node-tab .tab-count { font-size: 11px; }
		.metric-strip strong { font-size: 17px; }
		.detail-card p { font-size: 13px; line-height: 1.5; }
		.region-grid { grid-template-columns: repeat(2, 1fr); }
		.service-body { grid-template-columns: 260px 1fr; gap: 12px; }
		.service-summary strong { font-size: 18px; }
		.service-grid { gap: 8px; }
	}

	@media (max-width: 980px), (max-height: 680px) {
		.industry-chain-page { display: block; overflow: auto; padding-bottom: 24px; }
		.chain-grid { grid-template-columns: 1fr; height: auto; min-height: 0; }
		.chain-panel { min-height: 300px; }
		.panel-body { height: auto; min-height: 250px; }
		.upstream-body { min-height: 285px; }
		.service-panel { height: auto; min-height: 180px; margin-top: 12px; }
		.service-body { grid-template-columns: 1fr; height: auto; min-height: 150px; }
		.service-grid { grid-template-columns: repeat(2, 1fr); }
		.region-grid { grid-template-columns: repeat(2, 1fr); }
	}

	@media (min-width: 981px) and (max-height: 760px) {
		.industry-chain-page { grid-template-rows: minmax(0, 1fr) 138px; row-gap: 8px; padding-top: 6px; padding-bottom: 14px; }
		.panel-title { height: 32px; }
		.panel-title div { font-size: 18px; line-height: 27px; }
		.upstream-body { gap: 6px; padding: 10px 12px; }
		.origin-group { grid-template-columns: 72px 1fr; }
		.source-node { min-height: 60px; font-size: 14px; }
		.category-node { grid-template-columns: 60px 14px minmax(0, 1fr); min-height: 30px; gap: 5px; }
		.category-node span { width: 60px; height: 26px; font-size: 12px; }
		.sub-list em { min-width: 36px; padding: 2px 3px; font-size: 9px; }
		.function-body { gap: 8px; padding: 12px 14px; }
		.node-tab { padding: 4px 3px; }
		.node-tab .tab-name { font-size: 10px; }
		.node-tab .tab-count { font-size: 11px; }
		.metric-strip div { padding: 6px 4px; }
		.detail-card { padding: 10px 12px; }
		.detail-card h3 { margin-bottom: 6px; font-size: 16px; }
		.detail-card p { font-size: 13px; line-height: 1.45; }
		.category-pills { gap: 6px; }
		.pill { padding: 3px 8px; font-size: 10px; }
		.detail-section { padding: 8px 10px; }
		.section-head { font-size: 12px; margin-bottom: 6px; }
		.service-body { padding: 12px 16px; }
		.service-summary strong { font-size: 18px; }
		.service-summary .summary-desc { margin-bottom: 6px; font-size: 11px; }
		.summary-metrics div { padding: 6px 4px; }
		.summary-metrics b { margin-bottom: 4px; font-size: 10px; }
		.summary-metrics strong { font-size: 14px; }
		.service-card { padding: 9px 10px; }
	}
</style>
